// src/pages/Auth.js
import React, { useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  UserPlus,
  LogIn,
  Heart,
  Shield,
  Percent,
  Stethoscope,
} from "lucide-react";
import Login from "../components/auth/Login";
import Signup from "../components/auth/Signup";

const Auth = () => {
  const [activeTab, setActiveTab] = useState("login");
  const [loading, setLoading] = useState(false);

  const benefits = [
    {
      icon: Percent,
      title: "50% OFF Lab Tests",
      description: "Exclusive subscriber pricing on all diagnostic tests",
    },
    {
      icon: Stethoscope,
      title: "Priority Doctor Booking",
      description: "Quick appointments with verified healthcare professionals",
    },
    {
      icon: Heart,
      title: "Personal Health Tracking",
      description: "Monitor vitals, BMI, and health metrics over time",
    },
    {
      icon: Shield,
      title: "Free Home Collection",
      description: "Lab sample collection and medicine delivery at home",
    },
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Benefits Section */}
          <div className="space-y-8">
            <div className="text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start space-x-3 mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_healthcare-hub-25/artifacts/e5l587sq_GEETH365.jpg"
                  alt="GEETH365 Logo"
                  className="w-12 h-12 rounded-full border-2 border-orange-400 object-cover"
                />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">GEETH365</h1>
                  <p className="text-orange-600 font-medium">
                    Healthcare Platform
                  </p>
                </div>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Your Complete Healthcare Solution
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Join thousands of users who trust GEETH365 for their healthcare
                needs. Get instant access to premium features.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <Card
                  key={index}
                  className="border-orange-200 hover:shadow-lg transition-shadow"
                >
                  <CardContent className="p-6">
                    <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <benefit.icon className="w-6 h-6 text-orange-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-200">
              <h3 className="font-bold text-gray-900 mb-4 text-center">
                🎉 Free Subscription Included
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Badge className="bg-green-100 text-green-700 justify-center py-2">
                  Free Forever
                </Badge>
                <Badge className="bg-blue-100 text-blue-700 justify-center py-2">
                  Instant Benefits
                </Badge>
              </div>
            </div>
          </div>

          {/* Auth Forms */}
          <div className="flex items-center justify-center">
            <Card className="w-full max-w-md shadow-xl border-orange-200">
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl text-gray-900">
                  Get Started
                </CardTitle>
                <CardDescription>
                  Sign up for free or login to access your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="w-full"
                >
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger
                      value="login"
                      className="flex items-center space-x-2"
                    >
                      <LogIn className="w-4 h-4" />
                      <span>Login</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="signup"
                      className="flex items-center space-x-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Sign Up</span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    <Login
                      onToggle={handleTabChange}
                      loading={loading}
                      setLoading={setLoading}
                    />
                  </TabsContent>

                  <TabsContent value="signup">
                    <Signup
                      onToggle={handleTabChange}
                      loading={loading}
                      setLoading={setLoading}
                    />
                  </TabsContent>
                </Tabs>

                <div className="mt-6 text-center text-xs text-gray-500">
                  By signing up, you agree to our Terms of Service and Privacy
                  Policy
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
